/**
 * WhatsApp 24-hour customer-service window (ADR-025).
 *
 * Free-form replies are allowed for 24h after the candidate's last inbound
 * message. We stop SAFETY_MS short of the hard edge so a reply that is
 * composed at the boundary is never rejected by the provider.
 */
import moment from 'moment';

export const WINDOW_MS = 24 * 60 * 60 * 1000;
export const SAFETY_MS = 5 * 60 * 1000;
export const CLOSING_MS = 60 * 60 * 1000;

export type WindowLevel = 'open' | 'closing' | 'closed' | 'none';

export interface WindowState {
  level: WindowLevel;
  /** True when a free-form reply can still be sent. */
  open: boolean;
  /** Hard provider expiry (lastInboundAt + 24h); null when no inbound yet. */
  expiresAt: Date | null;
  /** Milliseconds of usable window left (already net of SAFETY_MS), >= 0. */
  remainingMs: number;
}

const NONE: WindowState = { level: 'none', open: false, expiresAt: null, remainingMs: 0 };

const toMs = (value?: string | Date | null): number | null => {
  if (value == null || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

export function getWindowState(
  lastInboundAt?: string | Date | null,
  now: number = Date.now(),
): WindowState {
  const lastMs = toMs(lastInboundAt);
  if (lastMs == null) return NONE;

  const expiresMs = lastMs + WINDOW_MS;
  const usableUntil = expiresMs - SAFETY_MS;
  const open = now < usableUntil;
  const remainingMs = Math.max(0, usableUntil - now);

  let level: WindowLevel = 'closed';
  if (open) level = remainingMs <= CLOSING_MS ? 'closing' : 'open';

  return { level, open, expiresAt: new Date(expiresMs), remainingMs };
}

/** '18h 22m' | '1h' | '42m' | '<1m' */
export function formatRemaining(ms: number): string {
  const safe = Math.max(0, Number(ms) || 0);
  if (safe < 60 * 1000) return '<1m';
  const hours = Math.floor(safe / (60 * 60 * 1000));
  const mins = Math.floor((safe % (60 * 60 * 1000)) / (60 * 1000));
  if (hours === 0) return `${mins}m`;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
}

/** 'today 2:03 PM' | 'tomorrow 2:03 PM' | 'Oct 9 2:03 PM' */
export function formatExpiry(expiresAt: Date, now: number = Date.now()): string {
  const target = moment(expiresAt);
  const ref = moment(now);
  const time = target.format('h:mm A');
  if (target.isSame(ref, 'day')) return `today ${time}`;
  if (target.isSame(ref.clone().add(1, 'day'), 'day')) return `tomorrow ${time}`;
  return `${target.format('MMM D')} ${time}`;
}
